import type { Tenant, TenantSuperadmin } from '@t4s/api-client/model'
import { type FormEvent, useId, useRef, useState } from 'react'
import { codeOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { Card, CardError, CardSkeleton } from './card.tsx'
import { formatInstantWithoutZone } from './instants.ts'

/**
 * Access — the superadmin table, docs/ux/PLATFORM-ADMIN.md §11.3.
 *
 * The one place this tier touches a tenant's users, so it is also the one place the **second
 * clock** appears (§7.5): a superadmin's last sign-in and their link's dates are the
 * **tenant's own records**, and are drawn in **that tenant's zone** — the column headers say
 * which, and each cell repeats it for a screen reader, because a bare time on a surface that
 * mixes two clocks is exactly the ambiguity **CF5** is about. (The page's other instants — the
 * tenant's Created — are the owner's.)
 *
 * Superadmins only. Employees and admins are not listed: a tenant with a working superadmin
 * needs nothing from this card, and a list of every user in every tenant is a surface with no
 * purpose and a large blast radius.
 *
 * **On a retired tenant every action is absent** — absent, not disabled. Retirement revoked
 * that tenant's sessions and its builds are refused, so a link minted afterwards would be a
 * credential redeemable only at an address that rejects every request: a control offered and
 * then refused, which this project's rules forbid. **A disabled account is the same case**:
 * the API refuses a link for it (`conflict`), so it gets a sentence and no button.
 */

export type SuperadminField = 'nickname' | 'fullName'

const PURPOSE_LABEL = { invite: 'Invite', password_reset: 'Password reset' } as const

const STATUS: Record<
  TenantSuperadmin['status'],
  { readonly label: string; readonly className: string }
> = {
  active: { label: 'Active', className: 'status status-ok' },
  pending: { label: 'Pending', className: 'status status-wait' },
  disabled: { label: 'Disabled', className: 'status status-off' },
}

/** "Invite · outstanding until 16 Oct 2026, 14:05" — or "Never" for an account with no link. */
function linkSentence(link: NonNullable<TenantSuperadmin['link']>, zone: string): string {
  const purpose = PURPOSE_LABEL[link.purpose]
  const at = (iso: string | null) => {
    const formatted = formatInstantWithoutZone(iso, zone)
    return formatted === '—' ? '' : ` ${formatted}`
  }
  switch (link.state) {
    case 'outstanding':
      return `${purpose} · outstanding until${at(link.expiresAt)}`
    case 'used':
      return `${purpose} · used${at(link.usedAt)}`
    case 'revoked':
      return `${purpose} · revoked${at(link.revokedAt)}`
    case 'expired':
      return `${purpose} · expired${at(link.expiresAt)}`
  }
}

type SuperadminListRead = {
  readonly data: readonly TenantSuperadmin[] | undefined
  readonly isPending: boolean
  readonly isError: boolean
  readonly error: unknown
  readonly refetch: () => void
}

export function AccessCard({
  tenant,
  superadmins,
  minting,
  onMint,
  onCreate,
  creating,
  createFieldErrors,
  onDiscardCreateFieldErrors,
}: {
  readonly tenant: Tenant | undefined
  readonly superadmins: SuperadminListRead
  readonly minting: boolean
  readonly onMint: (user: TenantSuperadmin) => void
  readonly onCreate: (input: { nickname: string; fullName: string }) => void
  readonly creating: boolean
  readonly createFieldErrors: Partial<Record<SuperadminField, string>>
  readonly onDiscardCreateFieldErrors: () => void
}) {
  const [adding, setAdding] = useState(false)
  const createButton = useRef<HTMLButtonElement | null>(null)
  const formId = useId()
  const retired = tenant?.state === 'retired'
  const zone = tenant?.timeZone
  const list = superadmins.data

  return (
    <Card
      wide
      title="Access"
      busy={tenant === undefined || superadmins.isPending}
      description="This tenant’s superadmins — the only users this console ever lists — and the way back in."
      action={
        tenant === undefined || retired ? undefined : (
          <button
            ref={createButton}
            type="button"
            className="button"
            aria-expanded={adding}
            aria-controls={adding ? formId : undefined}
            onClick={() => {
              setAdding(true)
              onDiscardCreateFieldErrors()
            }}
          >
            Create a superadmin
          </button>
        )
      }
    >
      {superadmins.isError ? (
        <CardError
          message={messageFor('tenant', codeOf(superadmins.error))}
          onRetry={superadmins.refetch}
        />
      ) : tenant === undefined || superadmins.isPending ? (
        <CardSkeleton lines={3} />
      ) : list !== undefined && list.length === 0 ? (
        <div className="card-body">
          <p>
            <strong>This tenant has no superadmin.</strong> Nobody can administer it, and it cannot
            fix that itself — minting an invite is an admin action inside the tenant.
          </p>
        </div>
      ) : list !== undefined && zone !== undefined ? (
        <div className="table-wrap">
          <table className="table table-stack">
            <caption className="visually-hidden">This tenant’s superadmin accounts</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Nickname</th>
                <th scope="col">Status</th>
                <th scope="col">Last sign-in · {zone}</th>
                <th scope="col">Most recent link · {zone}</th>
                {retired ? null : (
                  <th scope="col">
                    <span className="visually-hidden">Action</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {list.map((user) => (
                <tr key={user.userId}>
                  <td className="strong">{user.fullName}</td>
                  <td data-label="Nickname">
                    {user.nickname === null ? (
                      <span className="muted">—</span>
                    ) : (
                      <code>{user.nickname}</code>
                    )}
                  </td>
                  <td data-label="Status">
                    <span className={STATUS[user.status].className}>
                      {STATUS[user.status].label}
                    </span>
                  </td>
                  <td
                    className={user.lastLoginAt === null ? 'muted' : 'secondary'}
                    data-label={`Last sign-in · ${zone}`}
                  >
                    {user.lastLoginAt === null ? (
                      'Never'
                    ) : (
                      <>
                        {formatInstantWithoutZone(user.lastLoginAt, zone)}
                        <span className="visually-hidden"> {zone}</span>
                      </>
                    )}
                  </td>
                  <td
                    className={user.link === null ? 'muted' : 'secondary'}
                    data-label={`Most recent link · ${zone}`}
                  >
                    {user.link === null ? (
                      'Never'
                    ) : (
                      <>
                        {linkSentence(user.link, zone)}
                        <span className="visually-hidden"> {zone}</span>
                      </>
                    )}
                  </td>
                  {retired ? null : (
                    <td className="actions">
                      {user.status === 'disabled' ? (
                        <span className="muted small">
                          Not offered: a link would not let a disabled account in.
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="button button-sm"
                          disabled={minting}
                          onClick={() => {
                            onMint(user)
                          }}
                        >
                          {user.status === 'active'
                            ? 'Mint a password-reset link…'
                            : 'Mint a new invite…'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {retired ? (
        <div className="card-body">
          <p className="muted small">
            This tenant is retired, so no link can be minted for it. Letting somebody back into a
            retired tenant is not something this console can do.
          </p>
        </div>
      ) : null}

      {adding && !retired ? (
        <div className="card-body">
          <AddSuperadminForm
            id={formId}
            pending={creating}
            fieldErrors={createFieldErrors}
            onCancel={() => {
              setAdding(false)
              onDiscardCreateFieldErrors()
              createButton.current?.focus()
            }}
            onSubmit={onCreate}
          />
        </div>
      ) : null}
    </Card>
  )
}

function AddSuperadminForm({
  id,
  pending,
  fieldErrors,
  onCancel,
  onSubmit,
}: {
  readonly id: string
  readonly pending: boolean
  readonly fieldErrors: Partial<Record<SuperadminField, string>>
  readonly onCancel: () => void
  readonly onSubmit: (input: { nickname: string; fullName: string }) => void
}) {
  const nicknameId = useId()
  const nicknameErrorId = useId()
  const fullNameId = useId()
  const fullNameErrorId = useId()
  const [nickname, setNickname] = useState('')
  const [fullName, setFullName] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onSubmit({ nickname: nickname.trim(), fullName: fullName.trim() })
  }

  return (
    <form id={id} className="form" onSubmit={submit} noValidate>
      <div className="field">
        <label htmlFor={fullNameId}>Full name</label>
        <input
          id={fullNameId}
          value={fullName}
          required
          // The form was just asked for: the cursor belongs in it.
          // biome-ignore lint/a11y/noAutofocus: focus follows the control that opened this form
          autoFocus
          aria-invalid={fieldErrors.fullName !== undefined}
          aria-describedby={fullNameErrorId}
          onChange={(event) => {
            setFullName(event.target.value)
          }}
        />
        {fieldErrors.fullName === undefined ? null : (
          <p className="field-error" id={fullNameErrorId}>
            {fieldErrors.fullName}
          </p>
        )}
      </div>
      <div className="field">
        <label htmlFor={nicknameId}>Nickname</label>
        <input
          id={nicknameId}
          value={nickname}
          required
          aria-invalid={fieldErrors.nickname !== undefined}
          aria-describedby={nicknameErrorId}
          onChange={(event) => {
            setNickname(event.target.value)
          }}
        />
        {fieldErrors.nickname === undefined ? null : (
          <p className="field-error" id={nicknameErrorId}>
            {fieldErrors.nickname}
          </p>
        )}
      </div>
      <div className="confirm-controls">
        <button type="button" className="button" onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button
          type="submit"
          className="button button-primary"
          disabled={pending || nickname.trim() === '' || fullName.trim() === ''}
        >
          {pending ? 'Creating…' : 'Create and mint their link'}
        </button>
      </div>
    </form>
  )
}
